import React from 'react'
import { CheckboxChangeEvent } from 'primereact/checkbox'
import { DropdownChangeEvent } from 'primereact/dropdown'
import { InputNumberValueChangeEvent } from 'primereact/inputnumber'
import { RadioButtonChangeEvent } from 'primereact/radiobutton'
import { SliderChangeEvent } from 'primereact/slider'
import {
  EMkBookStepName,
  IMkBookDraft,
  IMkBookStepCompleteBooking,
  IMkBookStepPaymentOptions,
  IMkBookStepReservationBalance,
  IMkBookStepTravelerInfo,
  IMkBookStepVipUpgrades,
  IMkTripDetails,
  IMkBookInputField,
} from '@book/types'
import { EMkDepositRCIStepName, IMkAnyAccountField, IMkRCIDepositsDraft, IMkRCIDepositsSelectDepositStep } from '@account/types'
import { FullScreenModalProps, PrimeDialogPassThroughOptions } from '@components/ui/Dialog'

export type ModalConfig = Partial<
  PrimeDialogPassThroughOptions &
    Omit<FullScreenModalProps, 'isOpen' | 'onClose'> & {
      backdrop?: boolean
      closable?: boolean
      children?: React.ReactNode
      registerShell?: boolean
    }
>

export interface MkMapLocation {
  address1?: string
  address2?: string
  city?: string
  country?: string
  id: string
  image?: Image
  isUnavailable?: boolean
  location: {
    lat: number
    lng: number
  }
  name: string
  postalCode?: string
  state?: string
}

export type NavItem = {
  label: string
  url?: string
  items?: NavItem[]
  linkTarget?: string
}

export interface MenuItem {
  label?: string
  icon?: string
  url?: string
  items?: MenuItem[] | MenuItem[][]
  [key: string]: any // Sometimes includes utilities like templates or commands
}

export interface Image {
  alt?: string
  caption?: string
  height?: number
  url: string
  width?: number
}

export type IImage = Image

export type FooterItem = {
  label: string
  link: string
  target: string
}

/* ==== Enums ====  */
export enum EWaitlistRequestType {
  CREATE = 'create',
  MODIFY = 'modify',
  CONFIRM = 'confirm',
}

export enum EMkLoadingType {
  PAGE = 'page',
  MODAL = 'modal',
}

export enum EMkLocationTypeIcon {
  CITY = 'material-symbols:location-city',
  REGION = 'fa6-solid:map-location-dot',
  COUNTRY = 'material-symbols:home-pin-outline',
  CONTINENT = 'material-symbols:home-pin-outline',
  RESORT = 'material-symbols:king-bed',
  STATE = 'material-symbols:home-pin-outline',
}

export enum ELocationType {
  CITY = 'city',
  REGION = 'region',
  COUNTRY = 'country',
  CONTINENT = 'continent',
  RESORT = 'resort',
  STATE = 'state',
  QUERY = 'query',
}

export enum MkApiContext {
  LOCATION_AVAILABILITY = 'locationAvailability',
  CALENDAR_AVAILABILITY = 'calendarAvailability',
  BOOK_TRIP = 'bookReservation',
  JOIN_WAITLIST = 'joinWaitlist',
  MODIFY_TRIP_DATES = 'modifyTripDates',
  MODIFY_TRIP_TRAVELER = 'modifyTripTraveler',
  MODIFY_TRIP_POINTS_PROTECTION = 'modifyTripPointsProtection',
  MODIFY_TRIP_VIP_UPGRADE = 'modifyTripVipUpgrade',
  MODIFY_WAITLIST = 'modifyWaitlist',
  MODIFY_TRIP_HUB = 'modifyTripHub',
  MODIFY_COMPLETE = 'modifyComplete',
}

export enum EMkStepStatus {
  ACTIVE = 'active',
  COMPLETE = 'complete',
  INCOMPLETE = 'incomplete',
  DISABLED = 'disabled',
  ERROR = 'error',
}

export enum EMkInputFieldType {
  CHECKBOX = 'checkbox',
  DATE = 'date',
  EMAIL = 'email',
  HIDDEN = 'hidden',
  NUMBER = 'number',
  RADIO = 'radio',
  SELECT = 'select',
  SLIDER = 'slider',
  SWITCH = 'switch',
  TEL = 'tel',
  TEXT = 'text',
  TEXTAREA = 'textarea',
}

/* ==== Interfaces ====  */
export interface MkSavedSearchesResponse {
  data: MkSavedSearch[]
  message: {
    title: string
    subtitle: string
  }
}

export interface SaveSearchFilter {
  type: string
  values: string[]
}

export interface SaveSearchPayload {
  filters?: SaveSearchFilter[]
  id?: number
  name: string
  startDate?: string
  endDate?: string
  monthYears?: string
  numberOfNights?: number
  searchForFlexibleDates?: boolean
  monthsForFlexibleDates?: string[]
  allowApproximateDates?: boolean
  resortId?: string
  city?: string
  state?: string
  country?: string
  region?: string
  sortOrder?: string
  vacationExperiences?: string[]
  searchName?: string
  searchLocation?: string | ISearchLocation
  searchDateStart?: string
  searchDateEnd?: string
}

export interface MkSavedSearch {
  filters?: SaveSearchPayload['filters']
  id: number
  name: string
  startDate: string
  endDate: string
  monthYears: string
  numberOfNights: number
  flexibleDates: string
  resortId: string
  city: string
  state: string
  country: string
  region: string
  sortOrder: string
  vacationExperiences: string[]
  searchLocation?: string | ISearchLocation
  searchForFlexibleDates?: boolean
  monthsForFlexibleDates?: string[]
  allowApproximateDates?: boolean
}

export interface IMkLocationResultList {
  locations: {
    countries: {
      name: string
      regions: {
        name: string
        states: {
          name: string
          cities: {
            name: string
            resorts: {
              [x: string]: string
              resortName: string
              resortId: string
            }[]
          }[]
        }[]
      }[]
    }[]
  }[]
}

export interface ILocation {
  id: string
  name: string
  icon?: string
  title?: string
  subtitle?: string
  inputValue?: string
  locationType?: ELocationType
  resortName?: string
  resortId?: string
}

export interface IRegion {
  name: string
  locationPath: string
  imgPath: string
  imgAlt: string
  featured: boolean
  country: boolean
  resortCount?: number
}

export interface IRegionItem {
  brand?: string
  city?: string
  continent?: string
  country?: string
  latitude?: string
  longitude?: string
  region?: string
  resortId?: string
  resortName?: string
  state?: string
  locationType?: ELocationType
}

export interface ISearchLocation {
  id: string
  item: IRegionItem
  key: string
  query: string
  locationPath: string
  subHeading1?: string
  subHeading2?: string
  timestamp?: number
  title: string
}

export interface IMkMyTripsResponse {
  data: IMkTripDetails[]
  message: {
    title: string
    subtitle: string
  }
}

export interface ApiRequestConfig extends Omit<RequestInit, 'body'> {
  body?: Record<string, any>
  endpoint: string
  headers: Record<string, string>
  method: string | 'GET' | 'POST'
  params?: Record<string, any>
}

export interface IMkStep {
  conditionals: {
    [key: string]: boolean | undefined
  }
  content: {
    [key: string]: string | string[]
  }
  dynamicContent?: {
    [key: string]: Record<string, any> | any[]
  }
  fieldSet: {
    [key: string]: IMkBookInputField | IMkAnyAccountField
  }
  formState: {
    [key: string]: number | string
  }
  key: string
  name: string | EMkBookStepName | EMkDepositRCIStepName
  status: string | EMkStepStatus
  title: string
}

/**
 * StepBase and helpers
 *
 * Legacy note: Historically, step objects in the booking flow carried per-step
 * field definitions/state via `fieldSet` and `formState`. Newer flows (e.g.,
 * registration) centralize form state in their own contexts and do not use
 * these per-step fields, which led to registries filled with empty objects.
 *
 * This small helper allows both styles to coexist: callers may omit these keys
 * and we will inject harmless defaults. This keeps registries clean while
 * preserving compatibility with code that expects the keys to exist.
 *
 * TODO: Consider promoting a dedicated `StepBase` interface with optional
 * `fieldSet?` and `formState?`, and refactor step types across features to
 * extend it. For now we provide a minimal helper to avoid broad type churn.
 */
export type StepBase = Partial<Pick<IMkStep, 'fieldSet' | 'formState'>>

export function withStepDefaults<T extends { fieldSet?: any; formState?: any }>(step: T): T & { fieldSet: IMkStep['fieldSet']; formState: IMkStep['formState'] } {
  return {
    fieldSet: {},
    formState: {},
    ...step,
  } as any
}

export interface IMkInputField {
  disabled?: boolean
  // help?: string
  // icon?: string | undefined
  id: string // unique!!!
  isValid?: boolean | undefined
  hidden: boolean | undefined
  label: string
  name: string // used as the key on formState
  onChange?: (event: TMkChangeEvent) => void
  onHelpLinkClick?: () => void | undefined
  readonly?: boolean | undefined
  required?: boolean | undefined
  type: string | EMkInputFieldType
  validations?: Array<IMkInputFieldValidationRule> | undefined
  value?: MkGenericValue | undefined
  errorMessage: string
  paymentIcons?: {
    alt: string
    url: string
  }[]
  options: {
    disabled?: boolean
    value: string
    label: string
  }[]
  available?: string
  additionalValue?: string
  novalidate?: boolean
  onBlur?: () => void
  status?: 'error' | 'normal'
  statusText?: string
}

export interface IMkCheckboxField extends IMkInputField {
  checked?: boolean | undefined
}

export interface IMkNumberField extends IMkInputField {
  max?: number | undefined
  min?: number | undefined
}

export interface IMkRadioField extends IMkInputField {
  badge?: {
    label: string
  }
  checked?: boolean | undefined
  help?: string | undefined
  icon?: string | undefined
}

export interface ISelectOption {
  disabled?: boolean
  label: string
  value: string
}

export interface IMkSelectField extends IMkInputField {
  options: ISelectOption[]
  optionLabel?: string | undefined
  optionValue?: string | undefined
}

export interface IMkTextField extends IMkInputField {
  maxLength?: number | undefined
  minLength?: number | undefined
}

export type IFieldOptions = Pick<IMkInputField, 'novalidate' | 'onBlur' | 'status' | 'statusText'>

/* ==== Types ====  */
export type MkNavItem = {
  isActive?: boolean
  disabled?: boolean
  icon?: string
  label: string
  url?: string
  link?: string
  items?: Array<MkNavItem>
}

export type MkAccountNavItem = MkNavItem

export type MkPageProps = {
  kind: string
  slug: string
  title: string
  label: string
  url: string
  breadcrumbs: string
  parentKey: string
}

export type MkCountry = {
  countryCode: string
  countryName: string
  extension: string
}

export type MkState = {
  name: string
  value: string
}

export type MkConfig = {
  id: number
  key: string
  value: string
  children: MkConfig[]
  [x: string]: string | number | MkConfig | MkConfig[]
}

export type ExtractMapping = {
  [key: string]: { key: string; defaultValue: string }
}

export interface IInternationalPhoneChangeEvent {
  target: { name: string; value: string }
}

export type SocialLink = {
  iconPath: string
  link: string
  text: string
  id: number
}

export type TMkStep =
  | IMkBookStepVipUpgrades
  | IMkBookStepTravelerInfo
  | IMkBookStepReservationBalance
  | IMkBookStepPaymentOptions
  | IMkBookStepCompleteBooking
  | IMkRCIDepositsSelectDepositStep

export type IMkDraft = IMkBookDraft | IMkRCIDepositsDraft

export type TMkChangeEvent =
  | CheckboxChangeEvent
  | DropdownChangeEvent
  | RadioButtonChangeEvent
  | React.ChangeEvent<HTMLInputElement>
  | SliderChangeEvent
  | InputNumberValueChangeEvent
  | CustomEvent<any>
  | IInternationalPhoneChangeEvent

export type MkGenericValue = string | number | boolean | null | undefined

export type TMkGeneric = {
  [key: string]: MkGenericValue
}

export type IMkInputFieldValidationRule = {
  id?: string
  message?: string
  name: string
  passing: boolean
  regex?: RegExp
}

export interface IMkStepperContext {
  checkStepStatus: (step: TMkStep, status: EMkStepStatus) => boolean
  findStepIndex: (stepName: string) => number
  getActiveStep: () => TMkStep | undefined
  handleStepComplete: () => Promise<void>
  handleStepEdit: (step: TMkStep) => void
  handleStepInputFieldChange: (activeStep: TMkStep, fieldName: string, fieldValue: IMkInputField['value'], fieldType: string, opts?: { checked?: boolean }) => Promise<void>
}

// BreadcrumbItemProps for shared UI usage
export type BreadcrumbItemProps = {
  active?: boolean // default: false
  disabled?: boolean // default: false
  href?: string // default: undefined
  showIcon?: boolean // default: true
  value?: string // default: undefined
}

export interface ISuite {
  available: number
  cost: {
    amount: number
    currency: string
  }
  discount?: number
  floorPlan?: IImage
  id: string
  images: IImage[]
  name: string
  type: string
}

export interface ISuiteGroup {
  label: string
  suites: ISuite[]
}

export interface IVacationVibe {
  label: string
  icon: string
  iconSet?: string
}

export { Brand, type BrandDef, type BrandKey, brandDisplayName, brandSiteKey } from './Brand'
export * from './homePageContent'
export * from './mfa'

export interface IReview {
  author: {
    displayName: string
    firstName: string
    lastName: string
  }
  date: string
  rating: number
  review: string
}

export type IPageScript = {
  async?: boolean
  crossOrigin?: 'anonymous' | 'use-credentials'
  defer?: boolean
  id?: string
  referrerPolicy?: 'no-referrer' | 'no-referrer-when-downgrade' | 'same-origin' | 'origin' | 'strict-origin-when-cross-origin' | 'unsafe-url'
  src: string
  type?: string
}

export type IPageLink = {
  crossOrigin?: 'anonymous' | 'use-credentials'
  href: string
  hrefLang?: string
  media?: string
  referrerPolicy?: 'no-referrer' | 'no-referrer-when-downgrade' | 'same-origin' | 'origin' | 'strict-origin-when-cross-origin' | 'unsafe-url'
  rel: string
  sizes?: string
  type?: string
}

// A generic, recursive type for flexible config objects
export interface IComponentConfig {
  [key: string]: string | number | boolean | null | undefined | IComponentConfig | IComponentConfig[]
}

// A generic, recursive type for flexible config objects
export interface IContentConfig {
  [key: string]: string | number | boolean | null | undefined | IComponentConfig | IComponentConfig[]
}

export interface IPageConfig {
  components?: Record<string, IComponentConfig> // each page's components config will be drastically different but should still follow the same pattern (IComponentConfig)
  content?: Record<string, IContentConfig> // each page's content will be drastically different but should still follow the same pattern (IContentConfig)
  id: string
  links?: IPageLink[]
  meta?: {
    canonical?: string
    description?: string
    facebook?: {
      appId?: string
    }
    keywords?: string
    og?: {
      description?: string
      image?: {
        alt?: string
        height?: string
        secureUrl?: string
        url: string
        width?: string
      }
      locale?: string
      siteName: string
      title?: string
      type?: string
      url?: string
      video?: string // todo: elaborate on this, if/when requested
    }
    schema?: string[] // could be multiple schemas, and JSON will be a string and will need to be parsed when injected
    title: string
    twitter?: {
      app?: {
        id?: {
          googlePlay?: string
          ipad?: string
          iphone?: string
        }
        name?: {
          googlePlay?: string
          ipad?: string
          iphone?: string
        }
        url?: {
          googlePlay?: string
          ipad?: string
          iphone?: string
        }
      }
      card?: string
      description?: string
      image?: string
      imageAlt?: string
      site?: string
      title?: string
    }
  }
  scripts?: {
    body?: IPageScript[]
    gtm?: string
    head?: IPageScript[]
  }
}
